import { useState, useEffect } from 'react';
import { PayerName } from '@/lib/types';
import { PayerAlgorithmStep, generateAlgorithmSteps } from '@/lib/payerSimulation';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Brain, CheckCircle2, Loader2, ArrowRight, Zap, Activity } from 'lucide-react';

interface PayerAlgorithmVisualizerProps {
  payer: PayerName;
}

export function PayerAlgorithmVisualizer({ payer }: PayerAlgorithmVisualizerProps) {
  const [steps, setSteps] = useState<PayerAlgorithmStep[]>(() => generateAlgorithmSteps(payer));
  const [activeStep, setActiveStep] = useState(0);

  // Animate through steps
  useEffect(() => {
    setSteps(generateAlgorithmSteps(payer));
    setActiveStep(0);
  }, [payer]);

  useEffect(() => {
    if (activeStep < steps.length - 1) {
      const timer = setTimeout(() => {
        setActiveStep((prev) => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [activeStep, steps.length]);

  const getStatusIcon = (status: PayerAlgorithmStep['status'], index: number) => {
    if (index > activeStep) {
      return <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />;
    }
    
    switch (status) {
      case 'complete':
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-success" />;
      case 'adjusting':
        return <Zap className="w-5 h-5 text-warning" />;
      case 'probing':
        return <Loader2 className="w-5 h-5 text-primary animate-spin" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />;
    }
  };

  const getStatusColor = (status: PayerAlgorithmStep['status']) => {
    switch (status) {
      case 'complete':
      case 'success':
        return 'border-success/30 bg-success/5';
      case 'adjusting':
        return 'border-warning/30 bg-warning/5';
      case 'probing':
        return 'border-primary/30 bg-primary/5';
      default:
        return 'border-muted';
    }
  };

  return (
    <div className="glass-card-elevated p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Payer Algorithm Visualizer</h3>
        </div>
        <Badge className="bg-primary/20 text-primary border-primary/30 gap-1">
          <Activity className="w-3 h-3" />
          Active Model: {payer}
        </Badge>
      </div>

      {/* Model Header */}
      <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-secondary/80 to-secondary/40 border border-border/50">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Brain className="w-6 h-6 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">Active Payer Model: {payer} Orthopedic Division</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              AI probing payer logic • Real-time adaptation enabled
            </p>
          </div>
        </div>
      </div>

      {/* Algorithm Flow */}
      <div className="relative">
        {/* Vertical Line */}
        <div className="absolute left-[17px] top-6 bottom-6 w-0.5 bg-border/50" />
        
        <div className="space-y-4">
          {steps.map((step, index) => (
            <div 
              key={step.step}
              className={cn(
                "relative flex items-start gap-4 p-3 rounded-lg border transition-all duration-500",
                index <= activeStep ? getStatusColor(step.status) : "border-transparent bg-secondary/30 opacity-50"
              )}
            >
              {/* Status Icon */}
              <div className="relative z-10 flex-shrink-0 mt-0.5">
                {getStatusIcon(step.status, index)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm">{step.action}</span>
                  {index <= activeStep && (
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] uppercase tracking-wider",
                        step.status === 'success' && "border-success/30 text-success",
                        step.status === 'complete' && "border-success/30 text-success",
                        step.status === 'adjusting' && "border-warning/30 text-warning",
                        step.status === 'probing' && "border-primary/30 text-primary"
                      )}
                    >
                      {step.status}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{step.detail}</p>
              </div>

              {/* Arrow indicator for active step */}
              {index === activeStep && (
                <ArrowRight className="w-4 h-4 text-primary animate-pulse flex-shrink-0 mt-1" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Current Action Footer */}
      <div className="mt-6 pt-4 border-t border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs text-muted-foreground">
              Currently: {steps[activeStep]?.action || 'Complete'}
            </span>
          </div>
          <span className="text-xs font-mono text-muted-foreground">
            Step {activeStep + 1} of {steps.length}
          </span>
        </div>
      </div>
    </div>
  );
}
